import React from 'react'
import PropTypes from 'prop-types'
import { Form, Field } from 'react-final-form'
import Cookies from 'js-cookie'
import validate from 'validate.js'

import { Button, PasswordInput, InputField, Separator } from '@mc/design-system'
import { Trans, useTranslation } from '@mc/i18n'
import { paths } from '@mc/paths'
import { trackPasswordVisibilityToggled } from '@mc/pre-paywall-events'
import {
  track,
  Actions,
  Locations,
  PageSource,
  EventTypes,
} from '@mc/track-event'

import FacebookButton from '../FacebookButton'
import GoogleButton from '../GoogleButton'
import AppleButton from '../AppleButton'
import { isGiftAccountClarificationOn } from '../../utils'
import { i18nInstance } from '../../i18n/i18nInstance'
import { LOGIN_TITLE } from '../../constants'
import { EmailFormatWarning } from '../EmailFormatWarning'

const tf = i18nInstance.getFixedT(null, '@mc/authentication')

const constraints = {
  email: {
    presence: {
      message: tf('loginForm.email.presence'),
    },
    email: {
      message: tf('loginForm.email.valid'),
    },
  },
  password: {
    presence: {
      message: tf('loginForm.password.presence'),
    },
  },
}

type LoginFormProps = {
  onSubmit: () => void
  onForgotPasswordClick: () => void
  onRegisterClick: () => void
  facebookLogin: () => void
  appleLogin: () => void
  nextPage?: string
  thirdPartyError?: string
  userAgent?: string
}

const LoginForm = ({
  onSubmit,
  onForgotPasswordClick,
  onRegisterClick,
  facebookLogin,
  appleLogin,
  thirdPartyError,
  nextPage,
  userAgent,
}: LoginFormProps) => {
  const { t } = useTranslation('@mc/authentication')

  const onClickSignUp = () => {
    track(EventTypes.LOG_IN_MODAL_CTA_CLICKED, {
      action: Actions.SIGN_UP_CTA,
      location: Locations.LOG_IN_MODAL,
      href: paths.root,
      page_source: PageSource.LOG_IN,
    })

    onRegisterClick()
  }

  const initialValues = {
    email: Cookies.get('cart_email')?.replace(/['"]+/g, ''),
  }

  const isAppleTv = nextPage === '/appletv'

  return (
    <div>
      <h4 className='mc-text-h4 mc-text--center mc-mb-6'>{LOGIN_TITLE}</h4>

      {thirdPartyError && (
        <p className='mc-text--error mc-text--center mc-mb-4'>
          {thirdPartyError}
        </p>
      )}

      <div className='mc-mb-4'>
        <GoogleButton userAgent={userAgent}>
          {t('loginForm.google')}
        </GoogleButton>
      </div>

      <div className='mc-mb-4' data-ba='facebook-login'>
        <FacebookButton onLogin={facebookLogin} userAgent={userAgent}>
          {t('loginForm.facebook')}
        </FacebookButton>
      </div>

      <div className='mc-mb-4'>
        <AppleButton onLogin={appleLogin} />
      </div>

      <div className='mc-mb-4'>
        <p className='mc-text-x-small mc-text-hr mc-opacity--muted mc-text--uppercase mc-mt-3'>
          <span>{t('loginForm.or')}</span>
        </p>
      </div>

      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        validate={(values) =>
          validate(values, constraints, { fullMessages: false })
        }
        render={({ handleSubmit, submitting, submitError }) => (
          <form name='login-form' onSubmit={handleSubmit}>
            {!submitting && submitError && (
              <p
                aria-live='assertive'
                className='mc-text--error mc-text--center mc-mb-4'
              >
                {submitError}
              </p>
            )}

            <Field
              component={InputField}
              className='mc-mb-4'
              id='email'
              name='email'
              type='email'
              label='Email'
              data-ba='email-input'
              data-testid='email-input'
              autoComplete='email'
              required
            />

            <EmailFormatWarning
              pageSource={PageSource.LOG_IN}
              trackProps={{
                location: 'login_modal',
              }}
            />

            <Field
              component={PasswordInput}
              className='mc-mb-4'
              id='password'
              name='password'
              type='password'
              label='Password'
              data-ba='password-input'
              data-testid='password-input'
              autoComplete='current-password'
              required
              onTogglePassword={(visible: boolean) => {
                trackPasswordVisibilityToggled({
                  visible,
                  location: Locations.LOG_IN_MODAL,
                  pageSource: PageSource.LOG_IN,
                })
              }}
            />

            {isGiftAccountClarificationOn() ? (
              <Trans i18nKey='giftLoginTerms' ns='@mc/authentication'>
                <p className='mc-text-x-small mc-text--center mc-opacity--muted mc-my-4 mc-mx-6'>
                  {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
                  <a
                    className='mc-text--link mc-text--nowrap'
                    href='/privacy'
                  ></a>
                  {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
                  <a
                    className='mc-text--link mc-text--nowrap'
                    href='/terms'
                  ></a>
                </p>
              </Trans>
            ) : (
              <p className='mc-text-x-small mc-text--center mc-opacity--muted mc-my-4'>
                <Trans i18nKey='loginForm.terms' ns='@mc/authentication'>
                  <span className='mc-text--nowrap'>
                    <br></br>
                    {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
                    <a
                      className='mc-text--link mc-text--nowrap'
                      href='/privacy'
                    />
                    {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
                    <a
                      className='mc-text--link mc-text--nowrap'
                      href='/terms'
                    />
                  </span>
                </Trans>
              </p>
            )}

            <Button
              type='submit'
              loading={submitting}
              fullWidth
              data-ba='submit-btn'
              data-testid='submit-btn'
            >
              {t('loginForm.logIn')}
            </Button>
          </form>
        )}
      />

      <p className='mc-text--center mc-mt-4'>
        <a
          className='mc-text--link'
          href={paths.ssoLogIn(nextPage)}
          data-ba='sso-sign-in-button'
          target='_blank'
          rel='noreferrer'
        >
          {t('loginForm.organizationLogin')}
        </a>
      </p>

      {!isAppleTv && (
        <>
          {isGiftAccountClarificationOn() ? (
            <>
              <ForgotPasswordLink onClick={onForgotPasswordClick} />
              <Separator />
              <p className='mc-text--center mc-mt-4 mc-mx-8'>
                <Trans i18nKey='needAccount' ns='@mc/authentication'>
                  <button
                    className='mc-text--link'
                    onClick={onRegisterClick}
                  ></button>
                </Trans>
              </p>
            </>
          ) : (
            <p className='mc-text--center'>
              <Trans i18nKey='loginForm.needAnAccount' ns='@mc/authentication'>
                <button
                  className='mc-text--link mc-text--center'
                  onClick={() => onClickSignUp()}
                />
              </Trans>
            </p>
          )}
        </>
      )}
      {!isGiftAccountClarificationOn() && (
        <ForgotPasswordLink onClick={onForgotPasswordClick} />
      )}
    </div>
  )
}

const ForgotPasswordLink = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation('@mc/authentication')
  return (
    <p className='mc-text-x-small mc-text--center mc-opacity--muted mc-text--link mc-mt-4'>
      <button className='mc-text--underline' onClick={onClick}>
        {t('forgotPassword')}
      </button>
    </p>
  )
}

LoginForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onForgotPasswordClick: PropTypes.func.isRequired,
  onRegisterClick: PropTypes.func.isRequired,
  facebookLogin: PropTypes.func.isRequired,
  appleLogin: PropTypes.func.isRequired,
  nextPage: PropTypes.string,
  thirdPartyError: PropTypes.string,
  userAgent: PropTypes.string,
}

export default LoginForm
